import { useEffect, useState } from "react";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { Box, Button, Chip, Divider, LinearProgress, Sheet, Stack, Typography } from "@mui/joy";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../context/AuthContext";
import { fmt, useTranslation } from "../../../i18n";
import {
    AgentFeatures,
    fetchAgentFeatures,
    QuotaBlock,
    SubscriptionTier,
} from "../../../services/agentApi";
import {
    BillingConfig,
    BillingSubscription,
    fetchBillingConfig,
    fetchBillingSubscription,
    openBillingPortal,
    startCheckout,
} from "../../../services/billingApi";
import { CreditBalance } from "./CreditBalance";

/**
 * Settings → Plan & Usage.
 *
 * One `fetchAgentFeatures` call renders the user's effective tier
 * (personal tier, or a paying team's plan — shown as "via team X"),
 * a usage bar per metered dimension (AI asks + web searches per day,
 * task/note creations per month), and the plan's static limits
 * (message-history window, per-file upload size).
 *
 * `limit: null` = unlimited for this plan → "Unlimited" text, no bar.
 * The monthly blocks are absent entirely on older backends — rows
 * render only when the payload carries them, so this component is
 * safe to ship ahead of the API.
 *
 * Billing buttons render from `fetchBillingConfig`: free personal
 * tier → Stripe Checkout buttons per purchasable plan; an existing
 * personal subscription → the customer portal (plan changes go
 * through Stripe with proration — never a second checkout). With
 * Stripe unconfigured server-side the tab keeps the disabled
 * "coming soon" placeholder and plans stay operator-managed
 * (`feature_access set-tier` / `set-team-plan`).
 */

const TIER_COLOR: Record<SubscriptionTier, "neutral" | "primary" | "success" | "warning"> = {
    free: "neutral",
    core: "primary",
    pro: "primary",
    max: "success",
    enterprise: "warning",
};

// Self-serve plans, cheapest first — mirrors `PURCHASABLE_PLANS` in
// genos-api `origin/services/stripe_billing.py`. Each button still
// renders only if the server actually priced that plan
// (`billing.plans`), so an unpriced plan never offers a dead checkout.
const PURCHASABLE_PLANS = ["core", "pro", "max"] as const;

const UPGRADE_LABEL_KEY = {
    core: "upgradeToCore",
    pro: "upgradeToPro",
    max: "upgradeToMax",
} as const;

const UsageRow = ({
    label,
    windowLabel,
    remainingTemplate,
    block,
    unlimitedLabel,
}: {
    label: string;
    windowLabel: string;
    remainingTemplate: string;
    block: QuotaBlock;
    unlimitedLabel: string;
}) => {
    const unlimited = block.limit === null;
    // The bar shows what is LEFT of the allowance, not what has been
    // consumed — full at the start of the window, empty at the cap. Same
    // direction as the credit meter in `CreditBalance`; the two sit in
    // the same modal and must not drain opposite ways.
    //
    // `limit === 0` reads as 0% (nothing available), not 100%, so a plan
    // that grants none of something doesn't render a full bar.
    const remaining =
        unlimited || block.limit === 0 ? 0 : 1 - Math.min(block.used / block.limit!, 1);
    // Unchanged, and deliberately still keyed on being AT the cap rather
    // than on the bar being visually empty: the warning colour marks the
    // state the server refuses in, and inverting the fill must not move
    // what the colour means.
    const atCap = !unlimited && block.limit !== 0 && block.used >= block.limit!;
    return (
        <Box>
            <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={1}>
                <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                    {label}
                </Typography>
                <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                    {unlimited
                        ? unlimitedLabel
                        : fmt(remainingTemplate, {
                              // Clamped: the server can report `used`
                              // above `limit` (a quota lowered by a
                              // downgrade, or a race at the boundary),
                              // and "-3 of 50 left" is not a number to
                              // show anyone.
                              remaining: Math.max((block.limit ?? 0) - block.used, 0),
                              limit: block.limit ?? 0,
                              window: windowLabel,
                          })}
                </Typography>
            </Stack>
            {!unlimited && (
                <LinearProgress
                    color={atCap ? "warning" : "primary"}
                    sx={{ mt: 0.5 }}
                    thickness={4}
                    value={remaining * 100}
                    determinate
                />
            )}
        </Box>
    );
};

export const PlanUsageSection = ({ onNavigateAway }: { onNavigateAway?: () => void } = {}) => {
    const { accessToken } = useAuth();
    const { t, locale } = useTranslation();
    const navigate = useNavigate();
    const [data, setData] = useState<AgentFeatures | null>(null);
    const [failed, setFailed] = useState(false);
    const [billing, setBilling] = useState<BillingConfig | null>(null);
    const [subscription, setSubscription] = useState<BillingSubscription | null>(null);
    // In-flight guard for the checkout/portal buttons: the click ends
    // in a full-page navigation to Stripe, so the button stays busy
    // until the browser leaves (or an error surfaces below it).
    const [billingBusy, setBillingBusy] = useState(false);
    const [billingError, setBillingError] = useState<string | null>(null);

    useEffect(() => {
        if (!accessToken) return;
        let cancelled = false;
        void fetchAgentFeatures(accessToken).then((features) => {
            if (cancelled) return;
            setData(features);
            setFailed(features === null);
        });
        // Billing availability is independent of the usage payload —
        // a null config (old backend / Stripe unset) keeps the
        // "coming soon" placeholder.
        void fetchBillingConfig(accessToken).then((cfg) => {
            if (!cancelled) setBilling(cfg);
        });
        // Renewal/expiry row. Null (no billing account, no live
        // subscription, or any failure) just hides the row.
        void fetchBillingSubscription(accessToken).then((sub) => {
            if (!cancelled) setSubscription(sub);
        });
        return () => {
            cancelled = true;
        };
    }, [accessToken]);

    const runBillingAction = (action: () => Promise<void>) => {
        if (!accessToken || billingBusy) return;
        setBillingBusy(true);
        setBillingError(null);
        action().catch((e: unknown) => {
            // Success navigates away — only failures return here.
            setBillingError(
                e instanceof Error && e.message ? e.message : t.settings.planUsage.billingError
            );
            setBillingBusy(false);
        });
    };

    const p = t.settings.planUsage;

    if (failed) {
        return (
            <Sheet sx={{ p: 2, borderRadius: "lg" }} variant="outlined">
                <Typography level="body-sm">{p.loadError}</Typography>
            </Sheet>
        );
    }
    if (!data) {
        return (
            <Sheet sx={{ p: 2, borderRadius: "lg" }} variant="outlined">
                <LinearProgress size="sm" />
            </Sheet>
        );
    }

    const tierLabel = {
        free: p.tierFree,
        core: p.tierCore,
        pro: p.tierPro,
        max: p.tierMax,
        enterprise: p.tierEnterprise,
    }[data.tier];

    return (
        <Sheet sx={{ p: 2, borderRadius: "lg" }} variant="outlined">
            <Stack alignItems="center" direction="row" spacing={1} sx={{ mb: 0.5 }}>
                <WorkspacePremiumRoundedIcon />
                <Typography level="title-md">{p.heading}</Typography>
                <Box sx={{ flex: 1 }} />
                <Chip color={TIER_COLOR[data.tier]} size="sm" variant="soft">
                    {tierLabel}
                </Chip>
            </Stack>
            {data.tier_source === "team" && data.tier_team && (
                <Typography level="body-xs" sx={{ color: "text.tertiary", mb: 0.5 }}>
                    {fmt(p.viaTeam, { team: data.tier_team })}
                </Typography>
            )}
            <Typography level="body-xs" sx={{ mb: 1.5 }}>
                {p.description}
            </Typography>

            <Stack spacing={1.25}>
                {/* AI usage: the credit balance when credits are the
                    authoritative limit, the daily ask/search counters
                    otherwise. Never both — under credits those counters
                    still increment (Free's abuse breaker reads one) but
                    describe no limit the user is subject to, and two
                    limits on screen when one applies is a worse answer
                    than the old UI gave. Web search folds in: a search
                    is priced into the request's credits, so a separate
                    allowance would charge for it twice. */}
                {data.credits ? (
                    <CreditBalance credits={data.credits} tier={data.tier} hideUpgradeNote />
                ) : (
                    <>
                        <UsageRow
                            block={data.llm_ask}
                            label={p.aiAsks}
                            remainingTemplate={p.quotaRemaining}
                            unlimitedLabel={p.unlimited}
                            windowLabel={p.todaySuffix}
                        />
                        <UsageRow
                            block={data.web_search}
                            label={p.webSearches}
                            remainingTemplate={p.quotaRemaining}
                            unlimitedLabel={p.unlimited}
                            windowLabel={p.todaySuffix}
                        />
                    </>
                )}
                {data.task_create && (
                    <UsageRow
                        block={data.task_create}
                        label={p.tasksCreated}
                        remainingTemplate={p.quotaRemaining}
                        unlimitedLabel={p.unlimited}
                        windowLabel={p.monthSuffix}
                    />
                )}
                {data.note_create && (
                    <UsageRow
                        block={data.note_create}
                        label={p.notesCreated}
                        remainingTemplate={p.quotaRemaining}
                        unlimitedLabel={p.unlimited}
                        windowLabel={p.monthSuffix}
                    />
                )}
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            <Stack spacing={0.75}>
                <Stack alignItems="center" direction="row" justifyContent="space-between">
                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                        {p.messageHistory}
                    </Typography>
                    <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                        {data.message_retention_days == null
                            ? p.unlimited
                            : fmt(p.messageHistoryDays, {
                                  days: String(data.message_retention_days),
                              })}
                    </Typography>
                </Stack>
                <Stack alignItems="center" direction="row" justifyContent="space-between">
                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                        {p.maxFileSize}
                    </Typography>
                    <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                        {data.upload_max_mb == null
                            ? p.unlimited
                            : fmt(p.maxFileSizeMb, { mb: String(data.upload_max_mb) })}
                    </Typography>
                </Stack>
                {(() => {
                    // Renewal / expiry row for a live personal
                    // subscription. `cancel_at` wins over the period
                    // end when a cancellation is scheduled (Stripe sets
                    // both; cancel_at is the authoritative stop date).
                    if (!subscription) return null;
                    const willEnd =
                        subscription.cancel_at_period_end || subscription.cancel_at !== null;
                    const endTs = subscription.cancel_at ?? subscription.current_period_end;
                    if (endTs == null) return null;
                    const dateLabel = new Date(endTs * 1000).toLocaleDateString(locale, {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                    });
                    return (
                        <>
                            <Stack
                                alignItems="center"
                                direction="row"
                                justifyContent="space-between"
                            >
                                <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                    {willEnd ? p.planEnds : p.planRenews}
                                </Typography>
                                <Typography
                                    color={willEnd ? "warning" : undefined}
                                    level="body-sm"
                                    sx={willEnd ? undefined : { color: "text.tertiary" }}
                                >
                                    {dateLabel}
                                </Typography>
                            </Stack>
                            {willEnd && (
                                <Typography color="warning" level="body-xs">
                                    {p.cancelScheduled}
                                </Typography>
                            )}
                            {subscription.status === "past_due" && (
                                <Typography color="danger" level="body-xs">
                                    {p.pastDue}
                                </Typography>
                            )}
                        </>
                    );
                })()}
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            {billing?.enabled ? (
                <Stack spacing={1}>
                    {/* WRAPS. These are 3-4 buttons whose labels can't
                        shrink, so in a single nowrap row they overflowed the
                        panel and the tab panel turned that into a horizontal
                        scrollbar. `gap` rather than Stack's `spacing`, which
                        compiles to margins and lays out wrongly once a row
                        wraps. The explanatory hint moved OUT of this row —
                        it's prose, not an action, and it was the widest item
                        in it. */}
                    <Stack
                        direction="row"
                        sx={{ alignItems: "center", flexWrap: "wrap", gap: 1.5 }}
                    >
                        {/* Personal upgrades key off the user's OWN tier —
                            a team-granted effective tier doesn't hide them
                            (someone on team-pro may still buy personal max).
                            A user with an existing personal subscription
                            changes plans through the PORTAL (proration,
                            no duplicate subscription), never a second
                            checkout. An operator-set paid tier without a
                            Stripe account shows neither. */}
                        {billing.personal_tier === "free" && (
                            <>
                                {PURCHASABLE_PLANS.filter((plan) =>
                                    billing.plans.includes(plan)
                                ).map((plan) => (
                                    <Button
                                        key={plan}
                                        disabled={billingBusy}
                                        size="sm"
                                        variant={plan === "pro" ? "solid" : "soft"}
                                        onClick={() =>
                                            runBillingAction(() =>
                                                startCheckout(accessToken!, plan)
                                            )
                                        }
                                    >
                                        {p[UPGRADE_LABEL_KEY[plan]]}
                                    </Button>
                                ))}
                            </>
                        )}
                        {/* The portal renders for ANYONE with a Stripe
                            customer — including a free tier after a
                            cancellation ran out. A cancelled customer
                            still needs invoice history and a re-
                            subscribe path, and the /legal page points
                            here as the cancellation route. */}
                        {billing.has_billing_account && (
                            <Button
                                disabled={billingBusy}
                                size="sm"
                                variant="outlined"
                                onClick={() =>
                                    runBillingAction(() => openBillingPortal(accessToken!))
                                }
                            >
                                {p.manageBilling}
                            </Button>
                        )}
                        <Button
                            size="sm"
                            variant="plain"
                            onClick={() => {
                                // The settings modal would otherwise stay
                                // open on top of the plans page.
                                onNavigateAway?.();
                                navigate("/workspace/plans");
                            }}
                        >
                            {p.comparePlans}
                        </Button>
                    </Stack>
                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                        {billing.personal_tier !== "free" && billing.has_billing_account
                            ? p.manageBillingHint
                            : p.upgradeHint}
                    </Typography>
                    {billingError && (
                        <Typography color="danger" level="body-xs">
                            {billingError}
                        </Typography>
                    )}
                </Stack>
            ) : (
                <Stack direction="row" sx={{ alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
                    {/* Stripe not configured server-side (or old backend). */}
                    <Button size="sm" variant="solid" disabled>
                        {p.upgradeCta}
                    </Button>
                    <Button
                        size="sm"
                        variant="plain"
                        onClick={() => {
                            onNavigateAway?.();
                            navigate("/workspace/plans");
                        }}
                    >
                        {p.comparePlans}
                    </Button>
                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                        {p.upgradeComingSoon}
                    </Typography>
                </Stack>
            )}
        </Sheet>
    );
};
